import * as THREE from "three";
import { cloneModel, loadModel } from "@/player/ModelLoader";

export type TargetAnimState = "idle" | "fleeing" | "hit";

const MODEL_URL = "/models/target/glub.glb";

/**
 * Animated "digital monster" GLB (Quaternius' Glub, CC0 — see ASSETS.md) that
 * hovers/flies in place, replacing the earlier plain procedural gem. Clip
 * names come from the source rig (CharacterArmature|...).
 */
const CLIP_NAMES: Record<TargetAnimState, string> = {
  idle: "CharacterArmature|Flying_Idle",
  fleeing: "CharacterArmature|Fast_Flying",
  hit: "CharacterArmature|HitReact",
};

// Source GLB is human-sized; scale down to read as a small hovering creature
// against the arena's human-scale calibration (see CharacterModel.ts).
const MODEL_SCALE = 0.55;
const LOOP_CROSSFADE = 0.2;
const HIT_CROSSFADE = 0.05;

// Only these materials get the own/other tint overlay — leave the eyes alone
// so the creature stays readable/expressive rather than a flat glowing blob.
const TINTED_MATERIAL_NAMES = new Set(["Glub_Main", "Wings"]);

export class TargetModel {
  readonly root = new THREE.Group();

  private mixer: THREE.AnimationMixer | null = null;
  private actions = new Map<TargetAnimState, THREE.AnimationAction>();
  private currentAction: THREE.AnimationAction | null = null;
  private loopState: Exclude<TargetAnimState, "hit"> = "idle";
  private hitting = false;
  private ready = false;

  constructor(color: number, emissiveIntensity: number) {
    loadModel(MODEL_URL)
      .then((loaded) => this.onLoaded(cloneModel(loaded), color, emissiveIntensity))
      .catch((err) => console.error("[TargetModel] failed to load", err));
  }

  private onLoaded({ scene, animations }: { scene: THREE.Group; animations: THREE.AnimationClip[] }, color: number, emissiveIntensity: number): void {
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      const material = mesh.material as THREE.MeshStandardMaterial;
      if (material && TINTED_MATERIAL_NAMES.has(material.name)) {
        const tinted = material.clone();
        tinted.color = new THREE.Color(color);
        tinted.emissive = new THREE.Color(color);
        tinted.emissiveIntensity = emissiveIntensity;
        mesh.material = tinted;
      }
    });
    scene.scale.setScalar(MODEL_SCALE);
    this.root.add(scene);

    this.mixer = new THREE.AnimationMixer(scene);
    this.mixer.addEventListener("finished", (e) => {
      if (e.action === this.actions.get("hit")) {
        this.hitting = false;
        this.playLoop(this.loopState);
      }
    });

    for (const [state, clipName] of Object.entries(CLIP_NAMES) as [TargetAnimState, string][]) {
      const clip = THREE.AnimationClip.findByName(animations, clipName);
      if (clip) this.actions.set(state, this.mixer.clipAction(clip));
    }

    this.ready = true;
    this.playLoop(this.loopState);
  }

  setLoop(state: Exclude<TargetAnimState, "hit">): void {
    this.loopState = state;
    if (!this.hitting) this.playLoop(state);
  }

  playHit(): void {
    if (!this.ready) return;
    const action = this.actions.get("hit");
    if (!action) return;
    this.hitting = true;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = false;
    action.play();
    if (this.currentAction && this.currentAction !== action) {
      action.crossFadeFrom(this.currentAction, HIT_CROSSFADE, false);
    }
    this.currentAction = action;
  }

  private playLoop(state: Exclude<TargetAnimState, "hit">): void {
    if (!this.ready) return;
    const action = this.actions.get(state) ?? this.actions.get("idle");
    if (!action || action === this.currentAction) return;
    action.reset();
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.play();
    if (this.currentAction) action.crossFadeFrom(this.currentAction, LOOP_CROSSFADE, false);
    this.currentAction = action;
  }

  update(dt: number): void {
    this.mixer?.update(dt);
  }

  dispose(): void {
    this.mixer?.stopAllAction();
    this.root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      mesh.geometry?.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat?.dispose();
    });
  }
}
